import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import Icon from '@/components/HeroIcons'
import { ProfessionalAvatar } from '../../../../components/ProfessionalAvatar'
import { useStyles } from './useStyles'
import {
  ReservationCardPsyaliveProps,
  GoToConsultationDetailsParams,
} from './types'
import {
  getDateInFull,
  getDayPeriod,
  getTimeInHourAndMinute,
} from '@/utils/date'
import { Button, Tag } from '@conexasaude/hero-web'
import { Link, toast } from '@conexasaude/hero'
import { RenderIf } from '@/components/RenderIf'
import { useHistory } from 'react-router-dom'
import {
  confirmReservationConsultation,
  getPhotoProfessionalPsyAlive,
  recuseReservationConsultation,
} from '../../my-calls-services'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { verifyFreeAttendances } from '@/modules/Attendance/services'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { setCache } from '@/utils/cache'
import { redirectToCheckout } from '@/modules/Checkout/utils/redirectToCheckout'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import Attendance from '@/modules/Attendance/classes/Attendance'

const ReservationCardPsyalive: FunctionComponent<
  ReservationCardPsyaliveProps
> = ({
  professionalName,
  specialty,
  professionalId,
  readOnly,
  bodyOnly,
  email,
  cellphone,
  token,
  id,
  council,
  appointmentDate,
  appointmentDateFormat,
}) => {
  const history = useHistory()
  const styles = useStyles()
  const { showModal } = useModalContext()
  const { logAnalyticsEvent } = useFirebaseContext()
  const [professionalPsyAlivePhoto, setProfessionalPsyAlivePhoto] = useState('')
  const { clinic } = useAuthContext()
  const { setAttendance } = useAttendanceContext()
  const { specialities } = useMedicalSpecialitiesContext()

  const goToConsultationDetails = async ({
    id,
  }: GoToConsultationDetailsParams) => {
    history.push(`/meus-atendimentos/atendimento/grupo/${id}`)
  }
  const dateInFull = getDateInFull(appointmentDate, {
    reversed: true,
  })
  const handleCardClick = () => {
    if (readOnly) {
      return
    }
    goToConsultationDetails({ id })
  }

  const handleConfirmClick = async () => {
    const dateTratative = appointmentDateFormat.replace(/\//g, '-')
    const hasFreeAttendances = await verifyFreeAttendances({
      date: dateTratative.replace(/\s\d{2}:\d{2}$/, ''),
      preferenceType: 'from_speciality',
      professionalType: 'PSICOLOGO',
      attendanceType: 'AGENDADO',
      council: council?.split(' ')[0],
      specialityId: professionalId,
      rescheduleToken: token,
    })
    const speciality = specialities.find(s => s.id === 'PSICOLOGO')
    setAttendance(() => {
      const att = new Attendance(speciality)
      att.hour = dateInFull
      att.patientContact = { email, cellphone }
      att.scheduleId = id
      return att
    })
    setCache('has-free-attendance', hasFreeAttendances)

    const hasCheckoutEnabled = clinic?.flag?.PermiteConsultaParticular
    if (hasCheckoutEnabled && hasFreeAttendances === 'limitReached') {
      toast(
        'Realize o pagamento da consulta. Você atingiu  o limite de atendimentos gratuitos. Finalize esse agendamento, realizando o pagamento.',
        {
          variant: 'warning',
          autoClose: 5000,
          theme: 'dark',
        },
      )
      setTimeout(() => {
        redirectToCheckout({
          attendanceType: 'scheduled',
          professionalType: 'PSICOLOGO',
          push: history.push,
          hasPayment: clinic?.flag?.PermiteConsultaParticular,
          hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
        })
      }, 1000)
      return
    }
    if (hasCheckoutEnabled && hasFreeAttendances !== 'limitNotReached') {
      setTimeout(() => {
        redirectToCheckout({
          attendanceType: 'scheduled',
          professionalType: 'PSICOLOGO',
          push: history.push,
          hasPayment: clinic?.flag?.PermiteConsultaParticular,
          hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
        })
      }, 1000)
      return
    }
    try {
      await confirmReservationConsultation({
        professionalId,
        id,
        appointmentDate: appointmentDateFormat,
        email,
        phone: cellphone,
      })
      history.push(`/checkout/reserva-confirmada`)
      logAnalyticsEvent('Pac_Reserva_ConfirmarReserva')
    } catch (error) {
      const errorMessage =
        error?.response?.data?.msg || error?.response?.msg || error.message

      toast(errorMessage, {
        variant: 'danger',
        autoClose: 5000,
      })
    }
  }

  const handleRecuseClick = async () => {
    try {
      await recuseReservationConsultation(id?.toString())
      showModal('CANCELLED_RESERVATION', {
        title: 'Reserva recusada',
        persistent: true,
      })
      logAnalyticsEvent('Pac_Reserva_Recusar')
    } catch (error) {
      const errorMessage =
        error?.response?.data?.msg || error?.response?.msg || error.message

      toast(errorMessage, {
        variant: 'danger',
        autoClose: 5000,
      })
    }
  }

  const timeInHourAndMinute = getTimeInHourAndMinute(appointmentDate)
  const dayPeriod = getDayPeriod(appointmentDate)
  const photoProfessionalPsyAlive = useCallback(async () => {
    try {
      const photoProfessionalPsyAlive = await getPhotoProfessionalPsyAlive(
        professionalId.toString(),
      )
      setProfessionalPsyAlivePhoto(photoProfessionalPsyAlive)
    } catch (err) {
      console.log(err)
    }
  }, [professionalId])

  useEffect(() => {
    photoProfessionalPsyAlive()
  }, [photoProfessionalPsyAlive])

  return (
    <div>
      <div role="button" css={styles.cardContainer} onClick={handleCardClick}>
        <RenderIf condition={!bodyOnly}>
          <div css={styles.cardProfessionalInfo}>
            <ProfessionalAvatar
              picture={professionalPsyAlivePhoto}
              name={professionalName}
              speciality={specialty}
            />
          </div>
        </RenderIf>
        <div css={styles.cardConsultationInfo}>
          <div>
            <Tag
              type="neutral"
              style={{
                marginTop: '12px',
                borderRadius: '0.25rem',
                margin: '0.25rem',
                backgroundColor: '#CDC5FF',
                fontWeight: 600,
              }}
            >
              <p
                style={{
                  color: '#1900AF',
                }}
              >
                Horário reservado
              </p>
            </Tag>
          </div>
          <div css={styles.base}>
            <Icon name="calendar" size="24" themeColor={['neutral', '900']} />
            <span>{dateInFull}</span>
          </div>
          <div css={styles.base}>
            <Icon name="clock" size="24" themeColor={['neutral', '900']} />
            <span>
              {timeInHourAndMinute} ({dayPeriod})
            </span>
          </div>
          <div css={styles.base}></div>
        </div>
        <div css={styles.cardFooter}>
          <Link onClick={handleRecuseClick}>Recusar</Link>
          <Button size="sm" onClick={handleConfirmClick}>
            Confirmar reserva
          </Button>
        </div>
      </div>
    </div>
  )
}
export type { ReservationCardPsyaliveProps }
export { ReservationCardPsyalive }
